import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AlertController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  formStructureToHeightInRows,
  formToHeightInRows,
} from '../../utils/form.utils';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SanitizeInputDirective } from '../../directives/inputSanitize.directive';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ModalContainerComponent } from '../modal-container/modal-container.component';
import { IBreadcrumbs } from '../breadcrumbs/breadcrumbs.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, catchError, combineLatest, concatMap, firstValueFrom, from, isObservable, map, of, take, tap, throwError } from 'rxjs';
import { ResourcesStructure } from './formResourcesStructure';
import { LoggerService } from '../../../core/services/utils/logger.service';
import { IBuilding } from '../../models/be/building';
import { SpatialScope } from '../../models/be/spatial';
import { SpatialEditorComponent } from '../../../pages/core/spatial-tracking/spatial-editor.component';
import { createUuid } from '../../utils/uuid.util';
import { CreateCustomerPayload, UpdateCustomerPayload } from '../../models/be/customers';
import { CustomerService } from '../../../core/services/customer.service';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { IOrganization } from '../../models/be/organization';
import { IRoom } from '../../models/be/room';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../enums/ionicColors.enum';
import { UserService } from '../../../core/services/user.service';
import { IUser } from '../../models/be/user';
import { IMedicalHistory } from '../../models/be/medicalHistory';
import { SensorService } from '../../../core/services/sensors.service';
import { IDeviceFacilities } from '../../models/be/device';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { IonButton, IonCheckbox, IonContent, IonIcon, IonToggle } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { add } from 'ionicons/icons';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { UnitStructureCreated, UnitStructureDraft, UnitStructureSaveError, UnitStructureSaveSession } from '../../utils/unit-structure-save';

export type FormRequestBodyValue = string | number | boolean | Date | null;

function isNestedFormValue(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !(value instanceof Date);
}

function isRequestBodyValue(value: unknown): value is FormRequestBodyValue {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value) || value instanceof Date;
}

export function flattenFormData(
  source: Record<string, unknown>,
  resourceId?: string | null
): Record<string, FormRequestBodyValue> {
  const valuesList: Record<string, FormRequestBodyValue> = {};
  Object.entries(source).forEach(([firstLevel, firstValue]) => {
    if (isNestedFormValue(firstValue)) {
      Object.entries(firstValue).forEach(([secondLevel, secondValue]) => {
        if (isNestedFormValue(secondValue)) {
          Object.entries(secondValue).forEach(([thirdLevel, thirdValue]) => {
            if (isRequestBodyValue(thirdValue)) valuesList[thirdLevel] = thirdValue;
          });
        } else if (isRequestBodyValue(secondValue)) valuesList[secondLevel] = secondValue;
      });
    } else if (isRequestBodyValue(firstValue)) valuesList[firstLevel] = firstValue;
  });
  if (resourceId) valuesList['id'] = resourceId;
  return valuesList;
}


/////////////////////////////////////////////////////////////////////////////////////
//                                         _                                       //
//                                  ⛔ATTENZIONE⛔                                //
//                                         _                                       //
//                          Avere cura nel mettere le mani                         //
//                                  in questo codice.                              //
//                                         _                                       //
//                      RICORDARSI DI SPIEGARE LE VARIE FUNZIONI                   //
//                                         _                                       //
//                                       NOTE:                                     //
//   1) Chiedo venia per la porcata che è stata fatta con le sezioni dinamiche,    //
//      sono stato obbligato in quanto una gestione della form con il formArray    //
//      avrebbe comportato l'esistenza di un livello di form in più e purtroppo    //
//      Angular non permette lato html di condizionare l'esistenza della           //
//      direttiva formControlGroup pertanto avrei dovuto ripetere 2 volte una      //
//      stessa parte di codice. Per ulteriori info e chiarimenti chiedere a        //
//      Jacopo Stefanuto, baci e abbracci                                          //
//                                         _                                       //
/////////////////////////////////////////////////////////////////////////////////////


@Component({
  selector: 'generic-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    MatGridListModule,
    MatIconModule,
    MatDividerModule,
    MatCardModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatExpansionModule,
    MatSelectModule,
    MatRadioModule,
    MatTooltipModule,
    SanitizeInputDirective,
    MatAutocompleteModule,
    MatDatepickerModule,
    ModalContainerComponent,
    MatProgressSpinnerModule,
    IonContent,
    IonButton,
    IonIcon,
    IonCheckbox,
    IonToggle,
    SpatialEditorComponent
  ]
})
export class FormComponent implements OnDestroy {
  /**
   * @description Fa da se che è il cuore del componente, la form si basa su questo parametro
   */
  public formData: IFormData;
  /**
   * @description Se sono presenti sezioni che possono essere aggiunte o rimosse allora vedrò questo parametro valorizzato con la struttura base della sezione
   */
  public dynamicSection!: IFormStructure | null;
  /**
   * @description FormGroup che regola tutti i campi della form nel component
   */
  public formGroup!: FormGroup;
  /**
   * @description Oggetto contenente la struttura in formato IFormStructure e FormControl della sezione dinamica, perchè? Perchè se vengono rimosse tutte le sezioni devo essere poi in grado di ricrearle sulla base di qualcosa pertanto salvo la struttura base su un oggetto
   */
  public formGroupStructure!: FormControl;
  /**
   * @description Lista dei Breadcrumbs, dato che si andrà a valorizzare mediante i dati pesacati dalla rotta
   */
  public breadcrumbsList: IBreadcrumbs[];
  /**
   * @description Id della risorsa in oggetto se presente, se non presente si tratta allora della creazione di una risorsa e non della modifica
   */
  private resourceId: string | null = null;
  /**
   * @description Id di una eventuale risorsa secondaria, come per esempio il dottore nel caso del paziente
   */
  private secondaryResourceId: string | null = null;
  /**
   * @description Altezza della modale espressa in righe
   */
  public modalHeight: number = 1;
  /**
   * @description Stato dei pulsanti della form
   */
  public formStatus: IFormChangeEmitter = {
    canSaveDraft: false,
    canSaveData: false,
  };
  public showGenerateDataButton: boolean = false;
  public resource: IFormResource;
  public inputsScores: {
    [key: string]: {
      scores: {
        [key: string]: number[]
      },
      thresholds: {
        minimal: number[],
        moderate: number[],
        severe: number[]
      },
      maxScore: number
    }
  } | null = null;
  /**
   * Variabile utilizzata per la lettura dei campi allo stato iniziale, non leggo da FormData poichè soggetto a modifiche
   */
  public readonly structureHolder: IFormData;
  private initialSpatialTrackingEnabled = false;
  public spatialScope: SpatialScope | null = null;
  public spatialUnitName = '';
  public structureSaving = false;
  public structureSaveError = '';
  private structureSession?: UnitStructureSaveSession;
  public get structureReloadRequired(): boolean { return this.structureSession?.reloadRequired === true; }
  public get canDeleteStructureItems(): boolean { return this.requestsService.getCurrentUser()?.role === 'superuser'; }

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private alertController: AlertController,
    private route: ActivatedRoute,
    private router: Router,
    private loggerService: LoggerService,
    private customerService: CustomerService,
    private userService: UserService,
    private facilitiesService: FacilitiesService,
    private sensorService: SensorService,
    private toastService: ToastService,
    private cdRef: ChangeDetectorRef,
    private requestsService: RequestsService,
  ) {
    /* Aggiungo l'icona 'add' di Ionic per poterla utilizzare in form.component.html */
    addIcons({
      'add': add,
    });
    /* Creo una nuova istanza di ResourcesStructure, classe che mi servirà poi per andare a popolare la form sulla base della categoria della risorsa in oggetto */
    const currentResourceStructure = new ResourcesStructure();
    // Mergio sulla variabile resource i dati contenuti in 'data', definiti nella root.routes.ts e i dati presi dagli 'state' definiti in fase di navigazione
    this.resource = {
      ...this.route.snapshot.data['data'],
      ...this.router.currentNavigation()?.extras.state
    };
    this.filterRestrictedFormSections(currentResourceStructure);
    this.resource.toHideSections && this.checkToHideSections(currentResourceStructure)
    this.formData = currentResourceStructure.formResourcesStructure[this.resource.category].formStructure;
    this.showGenerateDataButton = !!currentResourceStructure.formResourcesStructure[this.resource.category].showGenerateDataButton;
    this.loadFormDataInputsObservables()
    const structureHolderResources = new ResourcesStructure();
    this.filterRestrictedFormSections(structureHolderResources);
    this.structureHolder = structureHolderResources.formResourcesStructure[this.resource.category].formStructure
    // Valorizzo la variabile della sezione dinamica (qualora presente)
    this.dynamicSection = currentResourceStructure.formResourcesStructure[this.resource.category].dynamicSection || null;
    // Calcolo l'altezza della modale
    this.modalHeight = formToHeightInRows(this.formData, this.translateService);
    (this.resource.breadcrumbs || []).forEach((breadcrumb) => {
      if (breadcrumb.showBreadCrumbSubject && this.resource.breadCrumbSubject) {
        breadcrumb.breadCrumbSubject = this.resource.breadCrumbSubject
      }
    })
    // Valorizzo i breadcrumbs
    this.breadcrumbsList = this.resource.breadcrumbs;
    this.loadFormValues();
  }

  private filterRestrictedFormSections(resources: ResourcesStructure): void {
    if (this.requestsService.getCurrentUser()?.role === 'superuser') {
      return;
    }

    const formStructure = resources.formResourcesStructure[this.resource.category]?.formStructure;
    formStructure?.structure.forEach((structure) => {
      structure.children = structure.children.filter((child) => !child.superuserOnly);
    });
  }

  /**
   * @description Funzione che si occupa di andare a nascondere le sezioni indicate su "toHideSections", SE VALORIZZATO
   * @param resourceStructure Struttura delle Risorse
   */
  checkToHideSections(resourceStructure: ResourcesStructure) {
    Object.keys(this.resource.toHideSections!).forEach((sectionName) =>
      (resourceStructure.formResourcesStructure[sectionName].formStructure.structure[0].children.filter((child) => (this.resource.toHideSections![sectionName] || []).includes(child.id)) || []).forEach((scale) => scale.hideSection = false)
    )
  }

  /**
   * @description Funzione che se presente input 'autocomplete' e suggestedItem di tipo Observable, rimane in ascolto del suggestedItem e tiene in memoria la lista iniziale 
   */
  loadFormDataInputsObservables() {
    this.formData.structure.forEach((structure) => {
      structure.children.filter((child) => !child.hideSection).forEach((child) => {
        child.inputs.forEach((input) => {
          // Se stiamo gestendo un input di tipo 'autocomplete' allora verifico se è necessario andare a popolare la lista di item suggeriti attraverso qualche chiamata
          if (input.type === 'autocomplete' && isObservable(input.suggestedItems)) {
            input.suggestedItems.pipe(
              tap((data: any) => {
                input.suggestedItems = data;
                (this.resource.autocompleteData ||= {})[input.id] = data;
              }),
              catchError((err: any) => {
                input.suggestedItems = [];
                throw err;
              })
            ).subscribe();
          }
        })
      })
    });
  }

  /**
   * @description Se presente chiamata di recupero risorsa dai dati della rotta, faccio la chiamata e valorizzo gli Input con i dati che mi vengono tornati 
   */
  loadFormValues() {
    /* Richiamo i dati degli Input se presenti e i punteggi delle varie scale, sempre se presenti */
    combineLatest([
      this.resource.data?.pipe(catchError(err => {
        this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.LOADING_DATA'), IonicColors.RED)
        return of(null)
      })) || of(null),
      this.resource.additionalParams?.scoreLoader?.pipe(catchError(err => {
        this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.LOADING_DATA'), IonicColors.RED)
        return of(null)
      })) || of(null)
    ]).pipe(
      take(1),
      tap(([inputsValue, inputsScores]) => {
        /* Se presente scoreLoader allora faccio la chiamata e setto il max score dei vari child della struttura */
        if (inputsScores) {
          this.inputsScores = inputsScores;
          /* Ciclo i punteggi delle scale e ne setto il maxScore */
          Object.keys(this.inputsScores!).forEach(key => {
            if (!!this.formData.structure[0].children.find((child) => child.id === key)) {
              this.formData.structure[0].children.find((child) => child.id === key)!.score = {
                current: 0,
                max: this.inputsScores?.[key].maxScore || 0,
                grade: 'none'
              }
            }
          });
        }
        /* Se inputsValue esiste allora valorizzo gli Input con i valori scesi dalla chiamata */
        if (inputsValue) {
          switch (this.resource.category) {
            case 'clinical':
            case 'psychological':
              /* Se valutazione clinica o psicologica allora vado a valorizzare gli input e i radio con i valori salvati */
              this.formData.structure.forEach((structureSection) => {
                structureSection.children.map((data) => ({
                  ...data,
                  inputs: data.inputs.map((input: any) => (input.inputValue = inputsValue[data.id]?.values?.[input.id] || null)) as unknown as ISelectInput[]
                }));
              });
              /* Poi carico il formGroup */
              this.loadFormGroup();
              break;
            case 'structure':
              this.resourceId = inputsValue.ward.id;
              this.spatialUnitName = inputsValue.ward.name;
              this.structureSession = new UnitStructureSaveSession(this.facilitiesService, inputsValue);
              if (this.requestsService.getCurrentUser()?.role === 'superuser') {
                this.spatialScope = {
                  customerId: Number(inputsValue.ward.customerId || this.requestsService.getCurrentUser()?.customer?.id),
                  buildingId: inputsValue.ward.buildingId || this.resource.additionalData?.['id'],
                  unitId: inputsValue.ward.id
                };
              }
              //Valorizzo gli Input del reparto/appartamento
              this.formData.structure.forEach((structureSection) => {
                if (!structureSection.dynamicChildren) {
                  structureSection.children.map((data) => ({
                    ...data,
                    inputs: data.inputs.forEach((input: any) => (input.inputValue = inputsValue.ward[input.id])) as unknown as ISimpleInput[],
                  }));
                } else {
                  const roomPlaceHolder = JSON.parse(JSON.stringify(structureSection.children[0]));
                  let compiledRooms: IFormStructureChildren[] = [];
                  (inputsValue.rooms).forEach((room: IRoom) => {

                    compiledRooms.push({
                      ...roomPlaceHolder,
                      id: room.id!,
                      entityId: room.id,
                      inputs: roomPlaceHolder.inputs.map((input: IInputArray | ISimpleInput) => {
                        if (input.type === 'inputArray') {
                          const bedPlaceHolder = JSON.parse(JSON.stringify(input.inputs[0]));
                          return {
                            ...input,
                            inputs: (room.beds || []).map((bed) => {
                              return {
                                ...bedPlaceHolder,
                                id: 'name',
                                entityId: bed.id,
                                clientId: bed.id,
                                inputValue: bed.name,
                                validators: [Validators.required, Validators.maxLength(255)]
                              }
                            })
                          }

                        } else {
                          return {
                            ...input,
                            id: 'name',
                            validators: [Validators.required, Validators.maxLength(255)],
                            inputValue: room.name
                          }
                        }
                      }),
                      validators: [Validators.required]
                    })
                  })
                  structureSection.children = compiledRooms;
                }
              });
              this.modalHeight = formToHeightInRows(this.formData, this.translateService);
              this.loadFormGroup();
              break;
            case 'deviceLink':
              this.resourceId = inputsValue.id;
              this.formData.structure.forEach((structureSection) => {
                structureSection.children.map((data) => ({
                  ...data,
                  inputs: data.inputs.forEach((input: any) => {
                    input.inputValue = inputsValue['facilities'][input.id].id as string || '';
                    // Se l'input è una select con autocompletamento basato su altri campi (loadOn) allora vedo di riempire la lista valori con la relativa chiamata a cui passerò il valore dell'Input a cui si vuol fare riferimento, se valorizzato
                    if (input.type === 'select' && input.loadOn && inputsValue[input.loadOn]) {
                      input.loader(inputsValue[input.loadOn]).pipe(
                        tap((data: IOrganization[]) => {
                          input.values = data;
                        })
                      ).subscribe();
                    }
                  }) as unknown as ISimpleInput[],
                }));
              });
              this.loadFormGroup();
              break;
            default:
              if (this.resource.category === 'customer') {
                this.initialSpatialTrackingEnabled = inputsValue.spatialTrackingEnabled ?? false;
              }
              this.resourceId = inputsValue.id;
              this.formData.structure.forEach((structureSection) => {
                structureSection.children.map((data) => ({
                  ...data,
                  inputs: data.inputs.forEach((input: any) => {
                    if (structureSection.id === 'doctor') {
                      input.inputValue = inputsValue['doctor']?.[input.id];
                      this.secondaryResourceId = inputsValue['doctor']?.['id']
                    } else {
                      input.inputValue = inputsValue[input.id];
                    }
                    // Se l'input è una select con autocompletamento basato su altri campi (loadOn) allora vedo di riempire la lista valori con la relativa chiamata a cui passerò il valore dell'Input a cui si vuol fare riferimento, se valorizzato
                    if (input.type === 'select' && input.loadOn && inputsValue[input.loadOn]) {
                      input.loader(inputsValue[input.loadOn]).pipe(
                        tap((data: IOrganization[]) => {
                          input.values = data;
                        })
                      ).subscribe();
                    } else if (input.completeSection && this.resource.additionalData) {
                      // Se invece abbiamo un input con autocompletamento allora verifico se i campi corrispondono a quelli relativi alla sezione dell'autocompletamento e setto a "true" se tutti i campi sono uguali
                      if (data.inputs.filter((input) => !(input as ICheckboxInput).completeSection).some((data) => !!this.resource.additionalData?.[data.id])) {
                        input.inputValue = data.inputs
                          .filter((input) => !(input as ICheckboxInput).completeSection)
                          .every((data) => !this.resource.additionalData?.[data.id] || (inputsValue[data.id] == this.resource.additionalData[data.id]))
                      } else {
                        input.inputValue = false;
                      }
                    }
                  }) as unknown as ISimpleInput[],
                }));
              });
              this.loadFormGroup();
              break;
          }
        } else {
          this.loadFormGroup();
        }
      }),
      catchError((err) => {
        this.loadFormGroup();
        throw err;
      })
    ).subscribe()
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  /**
   * @description Costruisce il formGroup sulla base della struttura della form
   */
  loadFormGroup() {
    this.loadLoaderValues();
    /* Variabile che se valorizzata va ad eliminare la sezione indicata, viene utilizzata per andare a mostrare o nascondere certe sezioni in funzione dello stato di un input (per esempio nella creazione di una org, tramite una spunta è possibile abilitare o meno la visibilità della form degli edifici) */
    let sectionNameToRemove: string = '';
    /* Creazione della form */
    this.formGroup = this.formBuilder.group(
      /* Per ogni elemento dell'array della struttura */
      this.formData.structure.reduce(
        (structureAccumulator, structureCurrentChild) => {
          /* Funzione per la creazione del fromgroup della struttura */
          const buildStructureGroup = (
            structure: IFormStructure
          ) => {
            /**
             * @description Funzione invocata per la creazione dei formControl degli Inputs
             * @param currentChild Figlio corrente
             */
            const buildInputs = (currentChild: IFormStructureChildren) => ({
              /* Infine creo i vari formControl sulla base degli input presenti al più basso livello della struttura */
              ...currentChild.inputs.reduce(
                (_accumulator, currentInput) => {
                  /* Se il parametro adjustSectionVisibility è valorizzato allora valorizzo la variabile removeSection con il nome della sezione indicata */
                  (currentInput as ICheckboxInput).adjustSectionVisibility && (sectionNameToRemove = (currentInput as ICheckboxInput).adjustSectionVisibility as string);
                  /* Se il parametro dynamicInputParams è valorizzato allora procedo a creare un formArray */
                  if (currentInput.type === 'inputArray') {
                    _accumulator[currentInput.id] = this.formBuilder.array(
                      (currentInput as IInputArray).inputs.map((input) => (
                        this.formBuilder.group({ [input.id]: [(input as ISimpleInput).inputValue ?? null, (input as ISimpleInput).validators || []] })
                      ))
                    )
                  } else {
                    if (currentInput.type === 'date') {
                      currentInput.inputValue = new Date(currentInput.inputValue as string)
                    }
                    if (currentInput.type !== 'simpleText' && !(currentInput as ICheckboxInput).adjustSectionVisibility) {
                      _accumulator[currentInput.id] = [
                        {
                          value: (currentInput as ISimpleInput | ICheckboxInput | IToggleInput | ISelectInput | IAutoComplete | IAvatarInput).inputValue ?? null,
                          disabled: !!((currentInput as ISimpleInput).disableOnEdit && this.resourceId)
                        },
                        (currentInput as ISimpleInput).validators || []
                      ];
                    }
                  }
                  return _accumulator;
                },
                {} as any
              )
            })
            /* Se abilitato flag di figli dinamici allora creo un formArray */
            if (structureCurrentChild.dynamicChildren) {
              structureAccumulator[structure.id] = this.formBuilder.array(
                /* Per ogni elemento dell'array dei figli della struttura creo un nuovo formGroup */
                structure.children.reduce((accumulator, currentChild) => {
                  if (currentChild.hideSection) {
                    return accumulator;
                  };
                  accumulator.push(this.formBuilder.group(buildInputs(currentChild)));
                  return accumulator;
                }, [] as FormGroup[])
              )
            } else {
              /* Creo un nuovo formGroup */
              structureAccumulator[structure.id] = this.formBuilder.group(
                /* Per ogni elemento dell'array dei figli della struttura creo un nuovo formGroup */
                structure.children.reduce((accumulator, currentChild) => {
                  if (currentChild.hideSection) {
                    return accumulator;
                  }
                  accumulator[currentChild.id] = this.formBuilder.group(buildInputs(currentChild));

                  return accumulator;
                }, {} as any)
              );
            }
          };
          /* Creo il formgroup della struttura */
          buildStructureGroup(structureCurrentChild);
          /* E se presente una sezione dinamica allora ne calcolo l'altezza e ne costruisco il formgroup */
          if (this.dynamicSection) {
            this.dynamicSection.heightInRows = formStructureToHeightInRows(
              this.dynamicSection,
              this.translateService
            );
            !structureAccumulator[this.dynamicSection.id] &&
              buildStructureGroup(this.dynamicSection);
          }
          /* Definisco l'altezza della struttura della form */
          structureCurrentChild.heightInRows = formStructureToHeightInRows(
            structureCurrentChild,
            this.translateService
          );
          /* Se la sezione permette l'aggiunta o di sezioni analoghe o la rimozione della stessa allora salvo lo stato della struttura in quanto in seguito ad eliminazione di tutte le sezioni devo essere in grado di poter recuperare la struttura base nell'eventualità di una riaggiunta della sezione */
          if (
            !this.formGroupStructure &&
            this.dynamicSection &&
            structureAccumulator[this.dynamicSection.id] &&
            this.dynamicSection.canAddAndRemove
          ) {
            this.formGroupStructure = structureAccumulator[this.dynamicSection.id];
          }
          return structureAccumulator;
        },
        {} as any
      )
    );
    /* Se è stata valorizzata la variabile allora rimuovo la sezione indicata dal formGroup e dalla struttura dati */
    if (sectionNameToRemove !== '') {
      this.formGroup.removeControl(sectionNameToRemove);
      this.formData.structure.some(
        (structure) => structure.id === sectionNameToRemove
      ) &&
        this.formData.structure.splice(
          this.formData.structure.findIndex(
            (structure) => structure.id === sectionNameToRemove
          ),
          1
        );
      this.modalHeight = formToHeightInRows(this.formData, this.translateService);
    }

    this.formData.structure.forEach((structure) => {
      const disabledOnChildren = (structure.children.filter((child) => child.disabledOn) || [])
      structure.children.forEach((child) => {
        child.inputs.forEach((input) => {
          switch (input.type) {
            case 'autocomplete':
              // Nel caso di Input autocomplete rimango in ascolto dell'Input e modifico la lista di elementi suggeriti 
              this.formGroup.get(structure.id)?.get(child.id)?.get(input.id)?.valueChanges.pipe(
                tap((value) => {
                  if (typeof value === 'string') {
                    input.suggestedItems = (this.resource.autocompleteData ||= {})[input.id].filter((option) => option.label.toLowerCase().includes(value.toLowerCase()));
                  }
                })
              ).subscribe()
              break;
            case 'select':
              // Se trovo loadOn valorizzato allora rimango in ascolto dei cambiamenti del valore dell'Input e richiamo il loadOn 
              input.loadOn && this.formGroup.get(structure.id)?.get(child.id)?.get(input.loadOn)?.valueChanges.pipe(
                tap((value) => {
                  typeof value === 'number' && input.loader?.(value).pipe(
                    tap((data) => {
                      input.values = data;
                    })
                  ).subscribe();
                })
              ).subscribe();
              // Se trovo un child il cui stato di disabilitazione dipende dal valore di un Input allora procedo a rimanere in ascolto del cambiamento di Input e in base alle regole definite nel parametro 'disabledOn', abilitare o disabilitare
              if (disabledOnChildren.some((obj) => obj.disabledOn?.inputId === input.id)) {
                const currentObj = disabledOnChildren.find((obj) => obj.disabledOn!.inputId === input.id)
                const manageDisabilitationStatus = (value?: string) => {
                  if (value === currentObj!.disabledOn!.value) {
                    this.formGroup.get(structure.id)?.get(currentObj!.id)?.disable();
                  } else {
                    this.formGroup.get(structure.id)?.get(currentObj!.id)?.enable();
                  }
                }
                manageDisabilitationStatus(this.formGroup.get(structure.id)?.get(child.id)?.get(disabledOnChildren.find((obj) => obj.disabledOn?.inputId === input.id)!.disabledOn!.inputId)?.value)
                this.formGroup.get(structure.id)?.get(child.id)?.get(disabledOnChildren.find((obj) => obj.disabledOn?.inputId === input.id)!.disabledOn!.inputId)?.valueChanges.pipe(
                  tap((value) => {
                    manageDisabilitationStatus(value)
                  })
                ).subscribe();
              }
              break;
            default:
              break;
          }
        })
      })
    })

    /* A ogni cambiamento della form censisco lo stato del pulsante di salvataggio e dei dati della form  */
    /* NOTA BENE: É un operazione che viene fatta molto frequentemente, LIMITARE la complessità del codice quanto più possibile!! */
    this.formGroup.valueChanges.subscribe((data) => {
      if (this.structureSaving || this.structureReloadRequired) return;
      this.formStatus = {
        canSaveDraft: !this.formGroup.valid
          ? this.checkEligibilityToSaveDraft()
          : true,
        canSaveData: this.formGroup.valid,
      };
    });
  }

  /**
   * @description Funzione che se presente input 'select' e 'loader' carica i valori della select a partire dalle chiamate dei loaders
   */
  loadLoaderValues() {
    this.formData.structure.forEach((structureSection) => {
      structureSection.children.filter((child) => !child.hideSection).map((child) => ({
        ...child,
        inputs: child.inputs.forEach((input, inputIndex) => {
          if (input.type === 'select' && !!input.loader) {
            if (!inputIndex) {
              input.isLoading = true;
              input.loader().pipe(
                take(1),
                tap((res) => {
                  input.values = res;
                  input.isLoading = false;
                }),
                catchError((err) => {
                  this.loggerService.error(err);
                  input.isLoading = false;
                  throw err;
                })
              ).subscribe()
            };
            input.inputValue && this.checkLoader(child.inputs as ISelectInput[], input.inputValue, inputIndex)
          };
        }),
      }));
    });
  }

  /**
   * @description Funzione che va a popolare la lista degli Input Select tramite loader
   * @param inputList Lista degli input della sezione
   * @param resourceId Id della risorsa selezionata
   * @param currentInputIndex Indice dell'input in oggetto (rispetto all'inputList)
   */
  checkLoader(inputList: ISelectInput[], resourceId: string, currentInputIndex: number) {
    /**
     * @description Cuore della funzione principale, si occupa di andare a riempire la lista di valori da mostrare nella select
     */
    function loadValues(input: ISelectInput, checkOccupationStatus: boolean = false) {
      input.isLoading = true;
      input?.loader?.(resourceId).pipe(
        take(1),
        tap((data) => {
          input.isLoading = false;
          if (checkOccupationStatus) {
            input!.values = data.map((bed) => ({
              ...bed,
              ...(bed.id === input.inputValue) ? {
                disabled: false
              } : {},
              label: `${bed.label} ${(bed.disabled && (bed.id !== input.inputValue)) ? '(OCCUPATO)' : ''}`
            }));
          } else {
            input!.values = data;
          }
        }),
        catchError((err) => {
          input.isLoading = false;
          throw err;
        })
      ).subscribe();
    }
    switch (inputList[currentInputIndex].id) {
      case 'building':
      case 'buildingId':
        inputList.filter((input: ISelectInput) => !['buildingId', 'building'].includes(input.id)).map((input: ISelectInput) => input.values = []);
        loadValues(inputList[currentInputIndex + 1]);
        break;
      case 'unit':
      case 'unitId':
        inputList.filter((input: ISelectInput) => !['buildingId', 'building', 'unit', 'unitId'].includes(input.id)).map((input: ISelectInput) => input.values = []);
        loadValues(inputList[currentInputIndex + 1]);
        break;
      case 'roomId':
      case 'room':
        inputList.filter((input: ISelectInput) => input.id === 'bedId').map((input: ISelectInput) => input.values = []);
        inputList[currentInputIndex + 1] && loadValues(inputList[currentInputIndex + 1], true);
        break;
      default:
        break;
    }
  }

  /**
   * @description Alla distruzione del component resetto la Form
   */
  ngOnDestroy() {
    this.formGroup?.reset();
  }

  displayFn(inputId: string, resourceId: string): string {
    return (this.resource.autocompleteData ||= {})?.[inputId]?.find((data) => data.id == resourceId)?.label || '';
  }

  /**
   * @description Funzione che si occupa di effettuare la navigazione alla pagina precedente la Form
   */
  navigateBack() {
    this.resource.breadcrumbs?.length && this.resource.breadcrumbs[this.resource.breadcrumbs.length - 2].routerLink && this.router.navigate([this.resource.breadcrumbs[this.resource.breadcrumbs.length - 2].routerLink!])
  }

  /**
   * @description Funzione di generazione dati Form
   */
  generateData() {
    this.formData.structure.forEach((section) => {
      section.children?.forEach((child) => {
        child.inputs?.forEach((input) => {
          if ((input as ISimpleInput | ISelectInput).generatedData?.length && !this.formGroup.get(section.id)?.get(child.id)?.get(input.id)?.value) {
            this.formGroup.get(section.id)?.get(child.id)?.get(input.id)?.setValue((input as ISimpleInput | ISelectInput).generatedData![Math.floor(Math.random() * (input as ISimpleInput | ISelectInput).generatedData!.length)])
          }
        })
      })
    })
  }

  private syncCurrentCustomerSpatialTracking(customerId: number, spatialTrackingEnabled: boolean): void {
    const currentUser = this.requestsService.getCurrentUser();
    if (currentUser?.customer?.id !== customerId) return;

    this.requestsService.saveCurrentUser({
      ...currentUser,
      customer: {
        ...currentUser.customer,
        spatialTrackingEnabled
      }
    });
  }

  private saveCustomer$(
    customerPayload: Record<string, FormRequestBodyValue>,
    spatialTrackingEnabled: boolean
  ): Observable<unknown> {
    const isSuperuser = this.requestsService.getCurrentUser()?.role === 'superuser';
    const showSuccess$ = (translationKey: string, partialFailure: boolean): Observable<unknown> => partialFailure
      ? of({ partialFailure })
      : from(this.toastService.showToast(this.translateService.instant(translationKey), IonicColors.GREEN));
    const showFailure$ = (translationKey: string, error: unknown): Observable<never> => from(
      this.toastService.showToast(this.translateService.instant(translationKey), IonicColors.RED)
    ).pipe(concatMap(() => throwError(() => error)));

    if (this.resourceId) {
      const customerId = Number(this.resourceId);
      customerPayload['id'] = customerId;
      const shouldUpdateSpatialTracking = isSuperuser
        && spatialTrackingEnabled !== this.initialSpatialTrackingEnabled;

      return this.customerService.editCustomer$(customerPayload as unknown as UpdateCustomerPayload).pipe(
        concatMap(() => {
          if (!shouldUpdateSpatialTracking) return of({ partialFailure: false });
          return this.customerService.updateSpatialTrackingEnabled$(customerId, spatialTrackingEnabled).pipe(
            tap(() => this.syncCurrentCustomerSpatialTracking(customerId, spatialTrackingEnabled)),
            map(() => ({ partialFailure: false })),
            catchError((error) => {
              this.loggerService.error('Customer updated, but spatial tracking update failed: ' + error);
              return from(this.toastService.showToast(
                this.translateService.instant('GENERAL.ERRORS.CUSTOMER_UPDATED_SPATIAL_TRACKING_FAILED'),
                IonicColors.RED
              )).pipe(map(() => ({ partialFailure: true })));
            })
          );
        }),
        concatMap((result) => showSuccess$('GENERAL.SUCCESS.RESOURCE_UPDATED_SUCCESFULLY', result.partialFailure)),
        catchError((error) => showFailure$('GENERAL.ERRORS.RESOURCE_UPDATE', error))
      );
    }

    delete customerPayload['id'];
    return this.customerService.createCustomer$(customerPayload as unknown as CreateCustomerPayload).pipe(
      concatMap((response) => {
        if (!isSuperuser || !spatialTrackingEnabled) return of({ partialFailure: false });
        return this.customerService.updateSpatialTrackingEnabled$(response.customerId, true).pipe(
          tap(() => this.syncCurrentCustomerSpatialTracking(response.customerId, true)),
          map(() => ({ partialFailure: false })),
          catchError((error) => {
            this.loggerService.error('Customer created, but spatial tracking enabling failed: ' + error);
            return from(this.toastService.showToast(
              this.translateService.instant('GENERAL.ERRORS.CUSTOMER_CREATED_SPATIAL_TRACKING_FAILED'),
              IonicColors.RED
            )).pipe(map(() => ({ partialFailure: true })));
          })
        );
      }),
      concatMap((result) => showSuccess$('GENERAL.SUCCESS.RESOURCE_CREATED_SUCCESFULLY', result.partialFailure)),
      catchError((error) => showFailure$('GENERAL.ERRORS.RESOURCE_CREATE', error))
    );
  }

  /**
   * @description Funzione invocata al salvataggio della form, in base al tipo di risorsa vengono effettuate operazioni diverse
   */
  async onSubmit() {
    if (this.resource.category === 'structure') {
      await this.submitStructure();
      return;
    }
    /**
     * @description Funzione che torna un oggetto compatibile con il body che si vuole inserire in una chiamata di Post
     * @returns Torna un oggetto in formato chiave valore
     */
    const formDataToRequestBody = (
      customObject?: Record<string, unknown>
    ): Record<string, FormRequestBodyValue> => flattenFormData(
      (customObject ?? this.formGroup.value) as Record<string, unknown>,
      customObject ? null : this.resourceId
    );
    let requestCall: Observable<unknown> = of(null);
    // In base al tipo di risorsa che voglio andare a salvare, valorizzo requestCall con la chiamata di salvataggio o di modifica da fare
    switch (this.resource.category) {
      case 'customer': {
        const spatialTrackingEnabled = this.formGroup
          .get('legal_entity')
          ?.get('experimental_features')
          ?.get('spatialTrackingEnabled')
          ?.value === true;
        const customerPayload = formDataToRequestBody();
        delete customerPayload['spatialTrackingEnabled'];
        requestCall = this.saveCustomer$(customerPayload, spatialTrackingEnabled);
        break;
      }
      case 'organization':
        if (this.resourceId) {
          requestCall = this.facilitiesService.putOrganization$({
            ...formDataToRequestBody() as unknown as IOrganization,
            customerId: Number(this.resource.additionalData?.['id'])
          });
        } else {
          requestCall = this.facilitiesService.postOrganization$({
            ...formDataToRequestBody() as unknown as IOrganization,
            customerId: Number(this.resource.additionalData?.['id'])
          });
        }
        break;
      case 'user':
        requestCall = (this.resourceId
          ? this.userService.editUser$(formDataToRequestBody() as unknown as IUser).pipe(
            tap(() => this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.RESOURCE_UPDATED_SUCCESFULLY'), IonicColors.GREEN)),
            catchError((err) => this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.RESOURCE_UPDATE'), IonicColors.RED))
          )
          : this.userService.createUser$(formDataToRequestBody() as unknown as IUser).pipe(
            tap(() => this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.RESOURCE_CREATED_SUCCESFULLY'), IonicColors.GREEN)),
            catchError((err) => this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.RESOURCE_CREATED'), IonicColors.RED))
          ));
        break;
      case 'building':
        requestCall = (this.resourceId
          ? this.facilitiesService.putBuildings$(formDataToRequestBody() as unknown as IBuilding).pipe(
            tap(() => this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.RESOURCE_UPDATED_SUCCESFULLY'), IonicColors.GREEN)),
            catchError((err) => this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.RESOURCE_UPDATE'), IonicColors.RED))
          )
          : this.facilitiesService.postBuildings$(formDataToRequestBody() as unknown as IBuilding).pipe(
            tap(() => this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.RESOURCE_CREATED_SUCCESFULLY'), IonicColors.GREEN)),
            catchError((err) => this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.RESOURCE_CREATE'), IonicColors.RED))
          ));
        break;
      case 'deviceLink':
        let payload = {} as IDeviceFacilities;
        Object.keys(this.formGroup.value['deviceLink']['deviceLink']).forEach((facilityType) => {
          const currentInput = this.formData.structure[0].children[0].inputs.find((input) => input.id === facilityType) as ISelectInput;
          const currentValue = currentInput && currentInput.values.find((value) => value.id === this.formGroup.value['deviceLink']['deviceLink'][facilityType]);
          currentValue && (payload[facilityType as 'room' | 'unit' | 'building'] = {
            id: currentValue.id,
            name: currentValue.label
          })
        });
        requestCall = this.sensorService.linkDevice$(this.resourceId || this.resource.additionalData!['id'] as string, payload).pipe(
          tap(() => this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.RESOURCE_LINKED_SUCCESFULLY'), IonicColors.GREEN)),
          catchError((err) => this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.RESOURCE_LINKED'), IonicColors.RED))
        );
        break;
      default:
        break;
    }
    // Nel dubbio resetto pure lo stato di attivazione del pulsante salva
    this.formStatus = {
      canSaveDraft: false,
      canSaveData: false,
    };
    // Sottoscrivo la chiamata salvata e al completamento della chiamata effettuo la navigazione alla pagina precedente
    await firstValueFrom(requestCall.pipe(
      tap(() => {
        this.loggerService.info("Request sended successfully, navigating to the previous page...");
        this.navigateBack();
      }),
      catchError((err) => {
        this.formStatus = {
          canSaveDraft: true,
          canSaveData: true,
        };
        throw err;
      })
    ));
  }

  /** Normalizes the structure form: names stay values, database IDs stay metadata. */
  private structureDraft(): UnitStructureDraft {
    const structure = this.formData.structure.find(section => section.id === 'rooms')!;
    const rooms = this.formGroup.get('rooms') as FormArray;
    return {
      name: this.formGroup.get('ward')?.get('wardName')?.get('name')?.value,
      rooms: structure.children.map((child, index) => {
        const controls = rooms.at(index) as FormGroup;
        const beds = controls.get('beds') as FormArray;
        const inputs = (child.inputs.find(input => input.type === 'inputArray') as IInputArray).inputs;
        return {
          clientId: child.id, id: child.entityId, name: controls.get('name')?.value,
          beds: inputs.map((input, bedIndex) => ({
            clientId: input.clientId ||= createUuid(), id: input.entityId, name: beds.at(bedIndex).get('name')?.value
          }))
        };
      })
    };
  }
  private structureEntityCreated(entity: UnitStructureCreated): void {
    if (entity.kind === 'unit') { this.resourceId = entity.id; return; }
    const rooms = this.formData.structure.find(section => section.id === 'rooms')!;
    const child = rooms.children.find(room => room.id === (entity.roomClientId || entity.clientId));
    if (!child) return;
    if (entity.kind === 'room') child.entityId = entity.id;
    else {
      const input = (child.inputs.find(input => input.type === 'inputArray') as IInputArray).inputs.find(bed => bed.clientId === entity.clientId);
      if (input) input.entityId = entity.id;
    }
  }
  private async submitStructure(): Promise<void> {
    if (this.structureSaving || this.structureReloadRequired) return;
    if (this.formGroup.invalid) { this.formGroup.markAllAsTouched(); return; }
    this.structureSaveError = '';
    const draft = this.structureDraft();
    const user = this.requestsService.getCurrentUser();
    const creating = !this.resourceId;
    this.structureSession ||= new UnitStructureSaveSession(this.facilitiesService, {
      ward: { name: '', customerId: Number(user?.customer?.id), organizationId: user?.organization?.id || '',
        buildingId: String(this.resource.additionalData?.['id'] || '') },
      rooms: []
    });
    this.structureSaving = true;
    this.formStatus = { canSaveData: false, canSaveDraft: false };
    this.formGroup.disable({ emitEvent: false });
    try {
      await this.structureSession.save(draft, this.canDeleteStructureItems, entity => this.structureEntityCreated(entity));
      await this.toastService.showToast(this.translateService.instant(creating
        ? 'GENERAL.SUCCESS.RESOURCE_CREATED_SUCCESFULLY' : 'GENERAL.SUCCESS.RESOURCE_UPDATED_SUCCESFULLY'), IonicColors.GREEN);
      this.navigateBack();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Richiesta non completata.';
      this.structureSaveError = detail + (error instanceof UnitStructureSaveError && error.reloadRequired
        ? ' Riaprire la pagina prima di riprovare: la creazione potrebbe essere già stata salvata.'
        : ' Alcune modifiche potrebbero essere già salvate; correggere il problema e riprovare.');
      this.loggerService.error(this.structureSaveError);
      await this.toastService.showToast(this.structureSaveError, IonicColors.RED, 6000, { plainText: true });
    } finally {
      this.structureSaving = false;
      this.formGroup.enable({ emitEvent: false });
      this.formStatus = { canSaveDraft: false, canSaveData: !this.structureReloadRequired && this.formGroup.valid };
    }
  }

  /**
   * @description Controlla se all'interno della struttura sono presenti requisiti per mostrare il pulsante 'salva bozza' e in caso verifica la validità del campo indicato
   * @returns Torna true se sono censiti campi che devono essere compilati per poter salvare la bozza, altrimenti false
   */
  checkEligibilityToSaveDraft(): boolean {
    /* Può sembrare un operazione ad elevata complessità ma consideriamo che stiamo ciclando una struttura prepopolata (this.data) che non sarà mai troppo pesante */
    return this.formData.structure.some((structureChildren) =>
      structureChildren.children.some(
        (child) =>
          child.requiredToSaveDraft &&
          this.formGroup
            .get(structureChildren.id)
            ?.get(child.id)
            ?.get(child.requiredToSaveDraft)?.valid
      )
    );
  }

  /**
   * @description Funzione invocata al click di una checkbox con parametro 'adjustSectionVisibility' a true
   * @param checkStatus Stato della checkbox
   */
  async changeDynamicSectionsVisibility(checkStatus: boolean) {
    const builtId = `${(this.dynamicSection as IFormStructure).id}_${JSON.stringify(Date.now() + Math.random()).replace(".", "")}`
    /* Se la checkbox ha stato true allora aggiungo la sezione dinamica alla form e alla struttura dati */
    if (checkStatus) {
      this.formData.structure.push({
        ...this.dynamicSection as IFormStructure,
        id: builtId
      });
      this.formGroup.addControl(
        builtId,
        this.formGroupStructure
      );
      this.modalHeight = formToHeightInRows(this.formData, this.translateService);
    } else {
      /* Altrimenti mostro un alert che avvisa che l'operazione scatenerà l'eliminazione delle sezioni indicate */
      const alert = await this.alertController.create({
        mode: 'md',
        cssClass: 'soft-alert-dialog',
        header: 'Attenzione',
        message:
          'Procedendo verranno eliminati gli edifici, procedere?',
        buttons: [
          {
            text: 'Annulla',
            cssClass: 'alert-btn-neutral',
            handler: () => {
              alert.dismiss();
            },
          },
          {
            text: 'Procedi',
            cssClass: 'alert-btn-danger',
            handler: () => {
              Object.keys(this.formGroup.value).filter((param) => param.includes(this.dynamicSection!.id)).forEach((param) => {
                this.formGroup.removeControl(param);
                let sectionIndex = this.formData.structure.findIndex(
                  (element) => element.id === param
                );
                if (sectionIndex > -1) {
                  this.formData.structure.splice(sectionIndex, 1);
                }
              })
              this.modalHeight = formToHeightInRows(this.formData, this.translateService);
              alert.dismiss();
            },
          },
        ],
      });
      await alert.present();
    }
  }


  calculateAccordionRowSpan(accordion: IFormStructureChildren) {
    return Math.ceil(accordion.inputs.reduce((acc, curr) => {
      if (curr.type === 'inputArray') {
        acc += (curr as IInputArray).inputs.reduce((inputArrayAcc, inputArrayCurr) => {
          inputArrayAcc += inputArrayCurr.length;
          return inputArrayAcc;
        }, 2)
      } else {
        acc += ((curr as ISimpleInput).length || 0)
      }
      return acc;
    }, 18) / 6);
  }

  /**
   * @description Una delle chicche della form, l'verload del metodo per l'aggiunta degli elementi, da richiamare per far fronte all'aggiunta di elementi tipo accordion, sezioni, ecc...
   * @param type Definisce il tipo di elemento che si vuole aggiungere, 'section', 'accordion' o 'inputArray'
   */
  addItem(type: 'section'): void;
  addItem(type: 'accordion', structure: IFormStructure): void;
  addItem(type: 'inputArray', structure: IFormStructure, inputArray: IInputArray, formArray: FormArray, children: IFormStructureChildren): void;
  addItem(type: 'section' | 'accordion' | 'inputArray', structure?: IFormStructure, inputArray?: IInputArray, formArray?: FormArray, children?: IFormStructureChildren) {
    if (this.structureSaving || this.structureReloadRequired) return;
    switch (type) {
      case 'section':
        const builtId = `${(this.dynamicSection as IFormStructure).id}_${JSON.stringify(Date.now() + Math.random()).replace(".", "")}`
        this.formData.structure.push({
          ...(this.dynamicSection as IFormStructure),
          id: builtId,
        });
        /* Aggiungo la sezione al formGroup */
        this.formGroup.addControl(
          builtId,
          this.formGroupStructure
        );
        break;
      case 'accordion':
        (this.formGroup.controls[structure!.id] as FormArray).push(
          this.formBuilder.group({
            name: [null, [Validators.required, Validators.maxLength(255)]],
            beds: this.formBuilder.array([
              this.formBuilder.group({ name: [null, [Validators.required, Validators.maxLength(255)]] })
            ])
          })
        )
        const newChild = this.structureHolder.structure.find((str) => str.id === structure?.id)?.children[0];
        if (newChild) {
          const child: IFormStructureChildren = JSON.parse(JSON.stringify(newChild));
          child.id = createUuid();
          (child.inputs.find(input => input.type === 'inputArray') as IInputArray).inputs.forEach(input => input.clientId = createUuid());
          structure!.children.push(child);
        }
        structure!.heightInRows! += 1;
        break;
      case 'inputArray':
        formArray?.push(this.formBuilder.group({ name: [null, [Validators.required, Validators.maxLength(255)]] }));
        const newInput = (this.structureHolder.structure
          .find((str) => str.id === structure?.id)?.children
          .find((child) => child.childrenType === 'accordion')?.inputs
          .find((input) => input.id === inputArray?.id) as IInputArray)
          .inputs[0];
        if (newInput) inputArray!.inputs.push({ ...newInput, clientId: createUuid() });
        children!.rowSpan = this.calculateAccordionRowSpan(children!);
        break;
      default:
        break;
    }
    structure && (structure.heightInRows = structure.children.reduce((acc, curr) => {
      acc += (!curr.isAccordionOpen ? 1 : this.calculateAccordionRowSpan(curr));
      return acc;
    }, 1));
    this.modalHeight = formToHeightInRows(this.formData, this.translateService);
  };

  removeItem(type: 'section', sectionId: string): void;
  removeItem(type: 'accordion', childrenIndex: number, structure: IFormStructure): void;
  removeItem(type: 'inputArray', inputIndex: number, structure: IFormStructure, formArray: FormArray, inputArray: IInputArray, children: IFormStructureChildren): void;
  removeItem(type: 'section' | 'accordion' | 'inputArray', indexOrId: number | string, structure?: IFormStructure, formArray?: FormArray, inputArray?: IInputArray, children?: IFormStructureChildren) {
    if (this.structureSaving || this.structureReloadRequired) return;
    const entityId = type === 'accordion' ? structure?.children[indexOrId as number]?.entityId : type === 'inputArray' ? inputArray?.inputs[indexOrId as number]?.entityId : undefined;
    if (entityId && !this.canDeleteStructureItems) {
      this.structureSaveError = 'La rimozione di stanze e letti salvati è disponibile solo ai superuser.';
      return;
    }
    switch (type) {
      case 'section':
        let sectionIndex = this.formData.structure.findIndex(
          (element) => element.id === indexOrId
        );
        if (sectionIndex > -1) {
          this.formData.structure.splice(sectionIndex, 1);
        }
        this.formGroup.removeControl(indexOrId as string);
        break;
      case 'accordion':
        structure!.children.splice(indexOrId as number, 1);
        (this.formGroup.controls[structure!.id] as FormArray).removeAt(indexOrId as number);
        break;
      case 'inputArray':
        formArray!.removeAt(indexOrId as number);
        inputArray!.inputs.splice(indexOrId as number, 1);
        children!.rowSpan = this.calculateAccordionRowSpan(children!);
        break;
      default:
        break;
    }
    structure && (structure.heightInRows = structure.children.reduce((acc, curr) => {
      acc += (!curr.isAccordionOpen ? 1 : this.calculateAccordionRowSpan(curr));
      return acc;
    }, 1));
    this.modalHeight = formToHeightInRows(this.formData, this.translateService);
  }

  onAccordionAction(accordionStatus: boolean, accordionField: IFormStructureChildren, structure: IFormStructure) {
    accordionField.isAccordionOpen = accordionStatus;
    accordionField.rowSpan = accordionStatus ? this.calculateAccordionRowSpan(accordionField) : 1;
    this.modalHeight = formToHeightInRows(this.formData, this.translateService);
    structure.heightInRows = structure.children.reduce((acc, curr) => {
      acc += (!curr.isAccordionOpen ? 1 : this.calculateAccordionRowSpan(curr));
      return acc;
    }, 1)
  }

  /**
   * @description Funzione invocata al check di una checkbox abilitata all'autocompletamento della sezione, autocompila la sezione conenente la checkbox in oggetto
   * @param sectionId Id della sezione
   * @param childrenId Id della sottosezione (children)
   * @param checkboxStatus Stato della checkbox
   */
  autoCompleteSection(sectionId: string, childrenId: string, checkboxStatus: boolean) {
    // Recupero la sezione della checkbox premuta
    const sectionParams = this.formGroup.get(sectionId)?.get(childrenId);
    // Per ogni parametro della sezione, sovrascrivo con il rispettivo valore trovato negli additionalData o, nel caso la spunta venga tolta, rimuovo i valori dei parametri della sezione
    Object.keys(sectionParams?.value || {}).forEach((field) => {
      if (checkboxStatus) {
        // Se la sezione in oggetto è una sezione dinamica allora l'autocompletamento lo faccio sulla base dell'organizzazione e non del cliente
        if (this.dynamicSection && sectionId.includes(this.dynamicSection.id)) {
          this.formGroup.value['organization']['organization_address'][field] && sectionParams?.get(field)?.setValue(this.formGroup.value['organization']['organization_address'][field])
        } else {
          this.resource.additionalData![field] && sectionParams?.get(field)?.setValue(this.resource.additionalData![field]);
        }
      } else {
        sectionParams?.get(field)?.setValue(null);
      }
    });
  }

  get isDynamicSectionsExisting(): boolean {
    return !!this.dynamicSection && this.formData.structure.some((structure) => structure.id.includes(this.dynamicSection?.id!))
  }
}

/* DTO */

/* Component Related */

export interface IFormResource {
  category: string;
  breadcrumbs: IBreadcrumbs[];
  data?: Observable<any>;
  /**
   * Contiene una lista di id di risorse caricate in fase di modifica per permettere di tener traccia, per esempio, degli edifici in fase di modifica organizzazione
   */
  preloadedData?: { [key: string]: string[] };
  /**
   * Può sembrare inutile ma è essenziale, tiene traccia della lista di dati delle autocomplete allo scopo di usarla per il confronto nel momento in cui si va a scrivere nell'input dell'autocomplete 
   */
  autocompleteData?: { [key: string]: any[] };
  additionalData?: { [key: string]: string | number };
  /**
   * Sezioni da nascondere
   */
  toHideSections?: { [key: string]: string[] };
  additionalParams?: {
    scoreLoader?: Observable<any>;
  };
  /**
   * Valorizzato, mostra la stringa indicata dopo i breadCrumb marcati con il parametro showBreadCrumbSubject
   */
  breadCrumbSubject?: string;
}

/* Main */

export interface IFormData {
  /**
   * Definisce il tipo di form:
   * @param simple Se si vuole la classica form con titolo della modale e le varie sezioni con titolo e inputs
   */
  formType: 'simple';
  /**
   * La struttura della form, ogni elemento dell'array è una SEZIONE (vedi creazione paziente: la sezione paziente e medico curante sono 2 structures)
   */
  structure: IFormStructure[];
}

export interface IFormStructure {
  /**
   * Titolo della modale
   */
  title: string;
  /**
   * Id della modale
   */
  id: string;
  /**
   * Definisce l'altezza della struttura in numero di righe
   */
  heightInRows?: number;
  /**
   * Figli (sezioni) della modale
   */
  children: IFormStructureChildren[];
  /**
   * Opzionale, se abilitato permette l'aggiunta di una sezione analoga (o la rimozione della stessa)
   */
  canAddAndRemove?: boolean;
  /**
   * Label della categoria, utilizzata per andare a scrivere il riferimento alla risorsa indicata nei pulsanti di aggiunta e rimozione sezione
   */
  categoryLabel?: string;
  /**
   * Regola la possibilità di aggiungere o rimuovere children dinamicamente
   */
  dynamicChildren?: boolean;
}

export interface IFormStructureChildren {
  /**
   * Id del figlio, necessario alla creazione della form
   */
  id: string;
  /** ID persistito, distinto dal nome del controllo e dall'identità della riga UI. */
  entityId?: string;
  /**
   * Titolo della sezione
   */
  title: string;
  /**
   * Condiziona lo stato disabilitato degli Input della sezione indicata al cambiamento di valore di un Input 
   */
  disabledOn?: {
    /**
     * Id del child contenente l'input da monitorare
     */
    childId: string;
    /**
     * Id dell'Input da monitorare 
     */
    inputId: string;
    /**
     * Valore dell'input per cui si nasconde la sezione
     */
    value: string;
  };
  /**
   * Flag opzionale che se abilitato mostra il punteggio della sezione in oggetto ed esegue di conseguenza le chiamate per il calcolo dello score
   */
  score?: {
    current: number;
    max: number;
    grade: 'minimal' | 'moderate' | 'severe' | 'none';
  };
  /**
   * Opzionale, viene valorizzato con il campo la cui compilazione è richiesta al fine di poter salvare in bozza la form
   */
  requiredToSaveDraft?: string;
  /**
   * Se true, la sezione viene resa disponibile esclusivamente ai superuser.
   */
  superuserOnly?: boolean;
  /**
   * Lista degli input
   */
  inputs: Array<
    ISimpleInput | ICheckboxInput | IToggleInput | ISelectInput | ISimpleText | IAutoComplete | IAvatarInput | IInputArray
  >;
  /**
   * Opzionale, se abilitato non tiene il considerazione la sezione in oggetto nella creazione della form
   */
  hideSection?: boolean;
  /**
   * Opzionale, censisce i campi di testo con a destra un valore (es: Fattore di correzione tempo mobilitazione: -50%)
   */
  textLines?: {
    type: 'textValue',
    text: string,
    value: string
  }[];
  /**
   * Definisce il tipo di visualizzazione del children, di default la visualizzazione classica
   * @param accordion Se accordion allora mostro una visualizzazione ad accordion (mat-expansion-panel)
   */
  childrenType?: 'accordion';
  /**
   * Censisce lo stato dell'Accordion
   */
  isAccordionOpen?: boolean;
  /**
   * Opzionale, Definisce il numero di righe che occupa il Child
   */
  rowSpan?: number;
}

/* Sub */

export interface ISimpleInput {
  /** Identità persistita e identità stabile della riga, per i letti della struttura. */
  entityId?: string;
  clientId?: string;
  /**
   * Id dell'Input, necessario alla creazione della form
   */
  id: string;
  /**
   * Tipo di input:
   * @param input Input semplice, quelli in cui inserisci la mail per intendersi
   */
  type: 'input' | 'date';
  /**
   * Lunghezza dell'input (fa riferimento al sistema di griglia a 8 colonne)
   */
  length: number;
  /**
   * Label dell'Input
   */
  label: string;
  /**
   * Opzionale, permette di disabilitare il campo in fase di modifica risorsa
   */
  disableOnEdit?: boolean;
  /**
   * Lista dei validatori da applicare all'Input
   */
  validators: any[];
  /**
   * Placeholder dell'Input
   */
  placeholder?: string;
  /**
   * Valore dell'Input, se non presente il campo sarà vuoto
   */
  inputValue?: string | Date;
  /**
   * Regola dell'Input
   * @param number Potremmo andare a inserire nell'Input solo numeri
   * @param capitalize Ogni parola inserita nella form avrà la prima lettera maiscola
   */
  inputRule?: IInputRule;
  /**
   * Permetta la disabilitazione del campo sulla base di un altro campo //TODO: Implementare logica
   */
  disableOn?: {
    field: string,
    mustExist: boolean,
    cannotBe: string[]
  }
  /**
   * Regola l'aggiunta e la rimozione della risorsa in oggetto (nel concreto fa comparire una x sull'input che rimuoverà l'input in oggetto e un pulsante aggiungi che aggiunge un nuovo input)
   */
  dynamicInputParams?: {
    containerName: string;
    buttonLabel: string;
  };
  /**
   * Opzionale, permette di indicare dei dati di esempio dai quali verrà pescato un valore random e settato come valore dell'Input (logica inserita per far fronte alla rottura di coglioni di inserire dati manualmente)
   */
  generatedData?: (string | number)[]
}

export interface IInputArray {
  /**
   * Id dell'Input, necessario alla creazione della form
   */
  id: string;
  /**
   * Tipo di input:
   * @param inputArray Array di Input
   */
  type: 'inputArray';
  /**
   * Testo del pulsante che permette l'aggiunta della risorsa in oggetto
   */
  buttonLabel: string;
  /**
   * Array di input, al momento permetto solo la gestione di Input semplici (tipo quello dell'email per intendersi)
   */
  inputs: Array<ISimpleInput>;
}

export interface ISelectInput {
  /**
   * Id dell'Input, necessario alla creazione della form
   */
  id: string;
  /**
   * Tipo di input:
   * @param select Input select, quelli che fanno comparire una lista di opzioni tra cui scegliere
   */
  type: 'select';
  /**
   * Lunghezza dell'input (fa riferimento al sistema di griglia a 8 colonne)
   */
  length: number;
  /**
   * Label dell'Input
   */
  label: string;
  /**
   * Valori da inserire nella lista di valori della select
   */
  values: Array<{
    id: string;
    label: string;
    disable?: boolean;
  }>;
  /**
   * Lista dei validatori da applicare all'Input
   */
  validators?: any[];
  /**
   * Valore dell'Input, se non presente il campo sarà vuoto
   */
  inputValue?: string;
  /**
   * Funzione che invocata permette il caricamento dei valori da mostrare nella select
   */
  loader?: (id?: string | number) => Observable<{ id: string, label: string, disabled?: boolean }[]>;
  /**
   * Da valorizzare solo qualora presente il 'loader', permette di ricaricare il loader alla valorizzazione di un altro input
   */
  loadOn?: string;
  /**
   * Se abilitata disabilita il servizio di traduzione a favori di nomi propri da utilizzare nelle select
   */
  disableTranslation?: boolean;
  /**
   * Parametro che valorizzato indica che i valori dell'Input sono in una fase di caricamento
   */
  isLoading?: boolean;
  /**
   * Parametro che regola la possibilità di resettare l'Input
   */
  allowClear?: boolean;
  /**
   * Opzionale, permette di indicare dei dati di esempio dai quali verrà pescato un valore random e settato come valore dell'Input (logica inserita per far fronte alla rottura di coglioni di inserire dati manualmente)
   */
  generatedData?: (string | number)[]
}

interface ICheckboxInput {
  /**
   * Id dell'Input, necessario alla creazione della form
   */
  id: string;
  /**
   * Tipo di input:
   * @param checkbox Input checkbox, quelli quadrati che puoi spuntare
   */
  type: 'checkbox';
  /**
   * Lunghezza dell'input (fa riferimento al sistema di griglia a 8 colonne)
   */
  length: number;
  /**
   * Label dell'Input
   */
  label: string;
  /**
   * Valore dell'Input, se non presente il campo sarà vuoto
   */
  inputValue?: boolean;
  /**
   * Opzionale, se valorizzato mostra la/e sezione/i indicata/e in funzione del valore dell'Input
   */
  adjustSectionVisibility?: string;
  /**
   * Opzionale, se abilitato completa i dati della sezioni con quelli trovati in 'AdditionalParams'
   */
  completeSection?: boolean;
  /**
   * Opzionale, censisce lo stato di abilitazione del campo
   */
  disabled?: boolean;
}

export interface IToggleInput {
  id: string;
  type: 'toggle';
  length: number;
  rowSpan?: number;
  label: string;
  description?: string;
  inputValue?: boolean;
  disabled?: boolean;
}

interface ISimpleText {
  /**
   * Id dell'Input, necessario alla creazione della form
   */
  id: string;
  /**
   * Tipo di input:
   * @param simpleText Testo generico
   */
  type: 'simpleText';
  /**
   * Lunghezza dell'input (fa riferimento al sistema di griglia a 8 colonne)
   */
  length: number;
  /**
   * Label dell'Input
   */
  label: string;
  /**
   * Se valorizzato mostra un pulsante di Info che alla pressione mostra il testo indicato nel parametro
   */
  infoText?: string;
}

interface IAutoComplete {
  /**
   * Id dell'Input, necessario alla creazione della form
   */
  id: string;
  /**
   * Tipo di input:
   * @param autocomplete Input di testo con autocomplete
   */
  type: 'autocomplete';
  /**
   * Lunghezza dell'input (fa riferimento al sistema di griglia a 8 colonne)
   */
  length: number;
  /**
   * Label dell'Input
   */
  label: string;
  /**
   * Lista dei validatori da applicare all'Input
   */
  validators: any[];
  /**
   * Valore dell'Input, se non presente il campo sarà vuoto
   */
  inputValue?: string;
  /**
   * Lista degli elementi suggeriti dall'autcomplete
   */
  suggestedItems: { id: string; label: string }[] | Observable<any>;
}

interface IAvatarInput {
  /**
   * Id dell'Input, necessario alla creazione della form
   */
  id: string;
  /**
   * Tipo di input
   */
  type: 'avatar';
  /**
   * Lunghezza dell'input (fa riferimento al sistema di griglia a 8 colonne)
   */
  length: number;
  /**
   * Label dell'Input
   */
  label?: string;
  /**
   * Valore dell'Input, se non presente il campo sarà vuoto
   */
  inputValue?: string;
}

interface IRadioSystem {
  /**
   * Lista dei testi nella prima riga della tabella radio
   */
  headers: string[];
  /**
   * Ogni value corrisponde ad un radio pertanto avremo l'id che ne permette l'identificazione nel formGroup e la label che altro non è che il testo del radio button
   */
  values: { label: string; id: string, inputValue?: number; }[];
  /**
   * Lista dei validatori da applicare all'Input
   */
  validators?: any[];
}

/* Other */

export interface IFormChangeEmitter {
  /**
   * Censito per sviluppo futuro, consente di salvare la bozza della risorsa in oggetto per finirne il completamento in seguito
   */
  canSaveDraft: boolean;
  /**
   * Regola la possibilità di andare a salvare la form
   */
  canSaveData: boolean;
}

export type IInputRule = 'number' | 'capitalize' | 'upperCase' | 'noSpaces';
